import React from "react";
import { FeatherChevronLeft, FeatherChevronRight } from "@subframe/core";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { Loader } from "../../../../ui/components/Loader";
import { Table } from "../../../../ui/components/Table";
import TableSkeletonRows from "../../../../shared/presentation/components/TableSkeletonRows";
import { formatDate, truncateText } from "../../../../shared/utils/formatters";
import {
  getPostedStatusLabel,
  getStoredAccountingBackend,
} from "../../../../config/accounting";
import {
  DebitNoteEntity,
  DebitNoteListStatus,
  PaginationInfo,
} from "../../domain";

interface DebitNoteTableProps {
  debitNotes: DebitNoteEntity[];
  loading: boolean;
  inlineLoading?: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  itemsPerPage: number;
  searchTerm: string;
  onReview: (debitNote: DebitNoteEntity) => void;
  onPageChange: (page: number) => void;
}

export const DebitNoteTable: React.FC<DebitNoteTableProps> = ({
  debitNotes,
  loading,
  inlineLoading = false,
  error,
  pagination,
  itemsPerPage,
  searchTerm,
  onReview,
  onPageChange,
}) => {
  return (
    <div className="flex w-full flex-col items-start">
      <Table
        header={
          <Table.HeaderRow>
            <Table.HeaderCell className="w-40">
              Debit Note Number
            </Table.HeaderCell>
            <Table.HeaderCell className="w-64">Contact</Table.HeaderCell>
            <Table.HeaderCell className="w-40">Invoice Number</Table.HeaderCell>
            <Table.HeaderCell className="w-40">Date</Table.HeaderCell>
            <Table.HeaderCell className="w-32">Amount</Table.HeaderCell>
            <Table.HeaderCell className="w-56">Status</Table.HeaderCell>
            <Table.HeaderCell className="w-32" />
          </Table.HeaderRow>
        }
      >
        {loading ? (
          <TableSkeletonRows rowCount={itemsPerPage} columnCount={7} />
        ) : inlineLoading ? (
          <Table.Row>
            <Table.Cell colSpan={7}>
              <div className="flex w-full items-center justify-center py-8">
                <Loader />
              </div>
            </Table.Cell>
          </Table.Row>
        ) : error ? (
          <Table.Row>
            <Table.Cell colSpan={7}>
              <div className="flex w-full items-center justify-center py-8">
                <span className="text-body font-body text-error-600">
                  {error}
                </span>
              </div>
            </Table.Cell>
          </Table.Row>
        ) : debitNotes.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={7}>
              <div className="flex w-full items-center justify-center py-8">
                <span className="text-body font-body text-neutral-500">
                  {searchTerm.trim()
                    ? "No debit notes match your search"
                    : "No debit notes found"}
                </span>
              </div>
            </Table.Cell>
          </Table.Row>
        ) : (
          debitNotes.map((debitNote) => (
            <DebitNoteTableRow
              key={debitNote.id}
              debitNote={debitNote}
              onReview={onReview}
            />
          ))
        )}
      </Table>

      {!loading && !inlineLoading && pagination && (
        <DebitNotePagination
          pagination={pagination}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

interface DebitNoteTableRowProps {
  debitNote: DebitNoteEntity;
  onReview: (debitNote: DebitNoteEntity) => void;
}

const DebitNoteTableRow: React.FC<DebitNoteTableRowProps> = ({
  debitNote,
  onReview,
}) => {
  const contactName = getDocumentName(debitNote);

  return (
    <Table.Row>
      <Table.Cell className="w-40">
        <span className="text-body-bold font-body-bold text-default-font truncate">
          {truncateText(getDocumentNumber(debitNote), 20)}
        </span>
      </Table.Cell>
      <Table.Cell className="w-64">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar size="small" square={true}>
            {contactName.charAt(0).toUpperCase()}
          </Avatar>
          <span className="text-body font-body text-default-font truncate">
            {truncateText(contactName, 35)}
          </span>
        </div>
      </Table.Cell>
      <Table.Cell className="w-40">
        <span className="text-body font-body text-default-font truncate">
          {truncateText(getLinkedInvoiceNumber(debitNote), 20)}
        </span>
      </Table.Cell>
      <Table.Cell className="w-40">
        <span className="whitespace-nowrap text-body font-body text-neutral-500">
          {formatDate(getDocumentDate(debitNote), "medium")}
        </span>
      </Table.Cell>
      <Table.Cell className="w-32">
        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
          {getDocumentAmount(debitNote)}
        </span>
      </Table.Cell>
      <Table.Cell className="w-56">
        <div className="flex flex-col items-start gap-1">
          <DebitNoteStatusChip debitNote={debitNote} />
          {debitNote.updated_at && (
            <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
              {getStatusDateLabel(debitNote)}
            </span>
          )}
        </div>
      </Table.Cell>
      <Table.Cell className="w-32">
        <div className="flex grow shrink-0 basis-0 items-center justify-center">
          <Button variant="neutral-primary" onClick={() => onReview(debitNote)}>
            Review
          </Button>
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

interface DebitNotePaginationProps {
  pagination: PaginationInfo;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const DebitNotePagination: React.FC<DebitNotePaginationProps> = ({
  pagination,
  itemsPerPage,
  onPageChange,
}) => {
  const currentPage = pagination.current_page || 1;
  const totalPages = pagination.total_pages || 0;

  if (totalPages <= 1) return null;

  return (
    <div className="flex w-full items-center justify-between mt-4 flex-shrink-0">
      <span className="text-body font-body text-subtext-color">
        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
        {Math.min(currentPage * itemsPerPage, pagination.total)} of{" "}
        {pagination.total} results
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          icon={<FeatherChevronLeft />}
        >
          Previous
        </Button>
        <div className="flex items-center gap-1">
          {getVisiblePages(currentPage, totalPages).map((pageNumber) => (
            <Button
              key={pageNumber}
              variant={
                currentPage === pageNumber
                  ? "brand-primary"
                  : "neutral-tertiary"
              }
              size="small"
              onClick={() => onPageChange(pageNumber)}
              className="min-w-[32px]"
            >
              {pageNumber}
            </Button>
          ))}
        </div>
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!pagination.has_more}
          icon={<FeatherChevronRight />}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const getVisiblePages = (currentPage: number, totalPages: number) => {
  return Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
    if (totalPages <= 5) return index + 1;
    if (currentPage <= 3) return index + 1;
    if (currentPage >= totalPages - 2) return totalPages - 4 + index;
    return currentPage - 2 + index;
  });
};

const getDocumentAmount = (debitNote: DebitNoteEntity): string => {
  const currencySymbol = debitNote.json?.currency_symbol || "";
  const fallbackTotal =
    Number(debitNote.json?.sub_total || 0) +
    Number(debitNote.json?.tax_total || 0);
  const total = debitNote.json?.total ?? fallbackTotal;
  const numericTotal = Number(total);

  if (Number.isFinite(numericTotal)) {
    return `${currencySymbol}${numericTotal.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  return "N/A";
};

const getDocumentName = (debitNote: DebitNoteEntity): string => {
  return debitNote.json?.customer_name || "Unknown Contact";
};

const getDocumentNumber = (debitNote: DebitNoteEntity): string => {
  return (
    debitNote.document_number ||
    debitNote.json?.debitnote_number ||
    debitNote.json?.debit_note_number ||
    debitNote.id.toString()
  );
};

const getLinkedInvoiceNumber = (debitNote: DebitNoteEntity): string => {
  return debitNote.json?.reference_number || "N/A";
};

const getDocumentDate = (debitNote: DebitNoteEntity): string => {
  return debitNote.json?.date || debitNote.created_at || "";
};

const getDebitNoteListStatus = (
  debitNote: DebitNoteEntity,
): DebitNoteListStatus => {
  if (String(debitNote.is_under_review ?? "0") === "1") return "under_review";
  if (debitNote.status === "1") return "posted";
  if (debitNote.status === "2") return "rejected";
  return "pending";
};

const DebitNoteStatusChip: React.FC<{ debitNote: DebitNoteEntity }> = ({
  debitNote,
}) => {
  const status = getDebitNoteListStatus(debitNote);

  switch (status) {
    case "posted":
      return (
        <Badge variant="success">
          {getPostedStatusLabel(getStoredAccountingBackend())}
        </Badge>
      );
    case "under_review":
      return <Badge variant="warning">Under Review</Badge>;
    case "rejected":
      return <Badge variant="error">Rejected</Badge>;
    case "pending":
    default:
      return <Badge variant="warning">Pending</Badge>;
  }
};

const getStatusDateLabel = (debitNote: DebitNoteEntity): string => {
  const status = getDebitNoteListStatus(debitNote);
  const formattedDate = formatDate(debitNote.updated_at || "", "medium");

  if (status === "rejected") return `Rejected at ${formattedDate}`;
  return `Modified ${formattedDate}`;
};
